/**
 * The calendars' colour rules (spec 2026-10-14 §4.1), shared by the Planner's DayCell and the home
 * WeekStrip so a date looks the same in both: blue only for today — a filled disc when picked, a blue
 * number otherwise — and the primary ink for another picked day. A filled day's dots take the
 * fill's ink so they still show; elsewhere they are quiet. Pure: tokens in, colours out.
 */
import { onColor } from '@/constants/shared/theme';

export interface DayLookTokens {
  colors: { text: { primary: string; secondary: string; quiet: string } };
  semantic: { interactive: { primary: string; primaryHover: string } };
}

export interface DayLook {
  /** The picked day's fill (a disc, or the home strip's cell box); undefined = none. */
  fill: string | undefined;
  /** The date number's ink. */
  ink: string;
  /** A weekday initial drawn OUTSIDE the fill (the Planner strip). Inside a fill, use `ink`. */
  initialInk: string;
  weight: '500' | '600';
  dotInk: string;
}

export function dayLook(
  { isToday, isSelected, isOutside = false }: { isToday: boolean; isSelected: boolean; isOutside?: boolean },
  { colors, semantic }: DayLookTokens,
): DayLook {
  if (isSelected) {
    const fill = isToday ? semantic.interactive.primary : colors.text.primary;
    const ink = onColor(fill);
    return {
      fill,
      ink,
      initialInk: isToday ? semantic.interactive.primaryHover : colors.text.primary,
      weight: '600',
      dotInk: ink,
    };
  }
  if (isToday) {
    return {
      fill: undefined,
      ink: semantic.interactive.primaryHover,
      initialInk: semantic.interactive.primaryHover,
      weight: '600',
      dotInk: colors.text.quiet,
    };
  }
  return {
    fill: undefined,
    ink: isOutside ? colors.text.quiet : colors.text.secondary,
    initialInk: colors.text.quiet,
    weight: '500',
    dotInk: colors.text.quiet,
  };
}
